import React from "react";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { Check } from "@dreamhub/designsystem/icons";
import StageProgressIcon from "@/components/shared/StageProgressIcon";
import OnboardingStageMenuItem from "./OnboardingStageMenuItem";

interface Props {
  contractId: string;
  stages: OnboardingStage[];
  currentStageId: string | null;
  selectedWorkflowId: string | undefined;
  onStageSelect: (stageId: string) => void;
  onWorkflowSelect: (workflowId: string) => void;
  isCompleted?: boolean;
}

const OnboardingStagesMenu: React.FC<Props> = ({
  contractId,
  stages,
  currentStageId,
  selectedWorkflowId,
  onStageSelect,
  onWorkflowSelect,
  isCompleted = false
}) => {
  const { t } = useTranslation();
  const currentStageOrder = stages.find(
    (stage) => stage.id === currentStageId
  )?.itemOrder;

  return (
    <div className="relative flex-1 min-h-0">
      <div
        className="max-h-full overflow-y-scroll py-2 pb-14 flex flex-col gap-4"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
        dir="rtl"
      >
        <div dir="ltr">
          {map(stages, (stage) => {
            const isCurrentStage = !isCompleted && stage.id === currentStageId;
            const isPreviousStage =
              isCompleted ||
              (currentStageOrder !== undefined &&
                stage.itemOrder < currentStageOrder);
            const hasSelectedWorkflow = Boolean(
              stage.workflows?.some((wf) => wf.id === selectedWorkflowId)
            );

            return (
              <OnboardingStageMenuItem
                key={stage.id}
                contractId={contractId}
                stage={stage}
                isCurrentStage={isCurrentStage}
                isPreviousStage={isPreviousStage}
                hasSelectedWorkflow={hasSelectedWorkflow}
                selectedWorkflowId={selectedWorkflowId}
                onWorkflowSelect={onWorkflowSelect}
                onStageSelect={onStageSelect}
                isCompleted={isCompleted}
              />
            );
          })}
          <div className="flex gap-2 py-1 items-center">
            {isCompleted ? (
              <StageProgressIcon isCurrent isPrevious={false} />
            ) : (
              <IconContainer size="5" color="tertiary">
                <Check />
              </IconContainer>
            )}
            <div className="flex items-center py-2 pl-3 pr-2 w-full">
              <Text variant="bodySecondary">
                {t("contracts.detailedView.onboarding.goLive")}
              </Text>
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(0deg, var(--surface-elevation-0-page-background, #121926) 0%, rgba(18, 25, 38, 0.00) 100%)"
        }}
      />
    </div>
  );
};

export default OnboardingStagesMenu;
